import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, Rocket, X } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Top-of-page trial banner shown to tenant admins.
 * - Trial active → orange/blue countdown + Upgrade CTA
 * - Trial expired → red "Storefront is read-only" + Upgrade CTA
 * - Active paid plan → nothing
 */
export default function TrialBanner() {
  const [trial, setTrial] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/trial-status");
        if (alive) setTrial(data);
      } catch { /* admin endpoint only; non-admin gets 403 */ }
    };
    load();
    const t = setInterval(load, 60_000);
    // Cross-component invalidation hook — AdminUpgrade emits this after a
    // successful upgrade so the banner refreshes without a full page reload.
    const onInvalidate = () => load();
    window.addEventListener("trial-status-invalidate", onInvalidate);
    return () => {
      alive = false; clearInterval(t);
      window.removeEventListener("trial-status-invalidate", onInvalidate);
    };
  }, []);

  if (!trial || !trial.is_trial || dismissed) return null;

  const days = trial.days_remaining;
  const expired = trial.is_expired;
  // Soft urgency colours
  const tone = expired
    ? { bg: "bg-red-50 border-red-300 text-red-900", icon: AlertTriangle }
    : days <= 1
    ? { bg: "bg-amber-50 border-amber-300 text-amber-900", icon: Clock }
    : { bg: "bg-blue-50 border-blue-300 text-blue-900", icon: Rocket };
  const Icon = tone.icon;

  return (
    <div
      className={`border ${tone.bg} px-4 py-2.5 flex items-center gap-3 text-sm mb-3`}
      data-testid="trial-banner"
    >
      <Icon size={16} className="shrink-0" />
      <div className="flex-1 min-w-0">
        {expired ? (
          <>
            <span className="font-medium" data-testid="trial-banner-status">
              {trial.expiry_reason === "subscription"
                ? "Subscription expired."
                : "Trial ended."}
            </span>{" "}
            {trial.expiry_reason === "subscription"
              ? "Your storefront is paused — customers can browse but checkout, OTP delivery, and AI tools are off until you renew."
              : "Your storefront is in read-only mode — customers can browse but checkout, OTP delivery, and AI tools are paused until you upgrade."}
          </>
        ) : (
          <>
            <span className="font-medium" data-testid="trial-banner-status">
              {days >= 1 ? `${Math.floor(days)} day${Math.floor(days) === 1 ? "" : "s"} left in trial` : "Less than a day left in trial"}
            </span>
            {" — "}upgrade any time before then to keep your store live.
          </>
        )}
      </div>
      <Link
        to="/admin/upgrade"
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-medium ${
          expired ? "bg-red-600 text-white" : "bg-[hsl(var(--kb-primary))] text-white"
        }`}
        data-testid="trial-banner-upgrade"
      >
        {trial.expiry_reason === "subscription" ? "Renew now" : "Upgrade now"}
      </Link>
      {!expired && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))]"
          aria-label="Dismiss"
          data-testid="trial-banner-dismiss"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
